import React from 'react'
import Input from 'components/input'
import Select from 'components/select'
import ColourPicker from 'components/colour-picker'
import Field from 'components/field'
import { NUMBER_COLUMN_TYPES, DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { IColumnTypes } from 'components/spreadsheet/types'

const DECIMAL_COLUMN_TYPES: IColumnTypes[] = ['float', 'percentage']

// The formatting settings shared by the column configuration UIs. Field names/types mirror the column
// model so a consumer can pass its draft/column straight through as `value` and route `onChange` patches
// to wherever it persists them.
export interface ColumnFormattingValue {
  thousandSeparator: boolean
  decimalPlaces: number
  dateFormat: string | null
  displayLink: boolean
  headerBackgroundColor: string
  headerTextColor: string
  exportWidth: number | null
}

interface ColumnTypeFormattingFieldsProps {
  kind: IColumnTypes
  value: ColumnFormattingValue
  onChange: (patch: Partial<ColumnFormattingValue>) => void
}

interface ColumnFormattingFieldsProps {
  value: ColumnFormattingValue
  onChange: (patch: Partial<ColumnFormattingValue>) => void
}

// The formatting settings a column's type needs (number/decimal/date/URL formatting), shown in the main
// configuration directly under the column type. Which fields show is derived
// from the column kind, matching the model's own rules; types with no such settings render nothing.
export const ColumnTypeFormattingFields: React.FC<ColumnTypeFormattingFieldsProps> = ({ kind, value, onChange }) => {
  const isNumber = NUMBER_COLUMN_TYPES.includes(kind)
  const isDecimal = DECIMAL_COLUMN_TYPES.includes(kind)
  const isDate = DATE_COLUMN_TYPES.includes(kind)

  return (
    <>
      {isNumber && (
        <Field label="Number Formatting" htmlFor="column-number-formatting">
          <Select
            inputId="column-number-formatting"
            options={[
              { value: 'Comma', label: 'Comma (1,000)' },
              { value: 'None', label: 'None (1000)' }
            ]}
            optionsSelected={[value.thousandSeparator ? 'Comma' : 'None']}
            onOptionClick={(option) => onChange({ thousandSeparator: option !== 'None' })}
            searchable={false}
          />
        </Field>
      )}
      {isDecimal && (
        <Field label="Decimal Places" htmlFor="column-decimal-places">
          <Input
            id="column-decimal-places"
            type="number"
            min={0}
            max={10}
            value={value.decimalPlaces}
            onChange={(event) => {
              const places = parseInt(event.target.value, 10)
              onChange({ decimalPlaces: Number.isNaN(places) ? 0 : places })
            }}
          />
        </Field>
      )}
      {isDate && (
        <Field label="Date Format" htmlFor="column-date-format">
          <Input
            id="column-date-format"
            type="text"
            value={value.dateFormat ?? ''}
            placeholder={kind === 'datetime' ? 'e.g. YYYY-MM-DD HH:mm:ss' : 'e.g. YYYY-MM-DD'}
            onChange={(event) => onChange({ dateFormat: event.target.value || null })}
          />
        </Field>
      )}
      {kind === 'link' && (
        <Field label="URL Formatting" htmlFor="column-url-formatting">
          <Select
            inputId="column-url-formatting"
            options={[
              { value: 'text', label: 'Full Link' },
              { value: 'icon', label: 'Link Icon' }
            ]}
            optionsSelected={[value.displayLink ? 'text' : 'icon']}
            onOptionClick={(option) => onChange({ displayLink: option === 'text' })}
            searchable={false}
          />
        </Field>
      )}
    </>
  )
}

// The collapsed Formatting section body: the generic presentation settings every column type has (header
// colours and export width). The type-specific settings live in ColumnTypeFormattingFields.
const ColumnFormattingFields: React.FC<ColumnFormattingFieldsProps> = ({ value, onChange }) => {
  return (
    <>
      <Field label="Column Header Background Colour">
        <ColourPicker
          color={value.headerBackgroundColor}
          onChange={(hex) => onChange({ headerBackgroundColor: hex })}
        />
      </Field>
      <Field label="Column Header Text Colour">
        <ColourPicker color={value.headerTextColor} onChange={(hex) => onChange({ headerTextColor: hex })} />
      </Field>
      <Field label="Column Header Export Width (cm)">
        <Select
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'manual', label: 'Manual' }
          ]}
          optionsSelected={[value.exportWidth !== null ? 'manual' : 'auto']}
          onOptionClick={(option) => onChange({ exportWidth: option === 'auto' ? null : 5 })}
          searchable={false}
        />
        {value.exportWidth !== null && (
          <Input
            className="mt-[5px]"
            type="number"
            min={0}
            value={value.exportWidth}
            onChange={(event) => {
              const width = parseInt(event.target.value, 10)
              onChange({ exportWidth: Number.isNaN(width) ? 0 : width })
            }}
          />
        )}
      </Field>
    </>
  )
}

export default ColumnFormattingFields
